import type { GameState } from '@full-elevator/sim';
import { Container, Sprite } from 'pixi.js';
import type { RoomKind } from '../ui/homeModel.js';
import type { LivePart } from '../ui/rooms.js';
import { ROOM_LIFE, TRAIN_DOOR_X } from '../ui/rooms.js';
import { seedOf } from './motion.js';
import { clockHands, screenGlow, sway, trainAway } from './roomMotion.js';
import { partTexture, SCALE } from './roomTextures.js';

/** Where a floor's room is drawn: its top-left corner, and px per room unit. */
export interface RoomFrame {
  readonly x: number;
  readonly y: number;
  readonly scale: number;
}

interface Moving {
  readonly floor: number;
  readonly part: LivePart;
  readonly sprite: Sprite;
  readonly seed: number;
}

/** From standing at the platform to gone past the right-hand wall, in room units. */
const TRAIN_RUN = 90;

/**
 * The rooms' moving parts (`ROOM_LIFE`), over the still pictures and under
 * everybody: leaves, screens, the lobby clock, the platform's train.
 */
export class RoomLife {
  readonly layer = new Container();
  private moving: Moving[] = [];
  private frames: readonly RoomFrame[] = [];
  /** When people appear on each floor, read once: the train's timetable. */
  private readonly arrivals = new Map<number, readonly number[]>();

  constructor(
    /** As faded as the rooms themselves. */
    private readonly alpha: number,
    /** Reduced motion: everything stands where the still drawing has it. */
    private readonly still: boolean,
  ) {}

  async load(kinds: readonly RoomKind[]): Promise<void> {
    const entries = kinds.flatMap((kind, floor) =>
      ROOM_LIFE[kind].map((part, index) => ({ floor, part, index })),
    );
    const textures = await Promise.all(entries.map(({ part }) => partTexture(part)));
    if (this.layer.destroyed) return;
    this.moving = entries.map(({ floor, part, index }, i) => {
      const sprite = new Sprite(textures[i]);
      const { box, pivot } = part;
      sprite.anchor.set((pivot.x - box.x) / box.width, (pivot.y - box.y) / box.height);
      sprite.alpha = this.alpha;
      sprite.angle = part.rest;
      this.layer.addChild(sprite);
      return { floor, part, sprite, seed: seedOf(`${floor}:${index}`) };
    });
    this.layout(this.frames);
  }

  layout(frames: readonly RoomFrame[]): void {
    this.frames = frames;
    for (const { floor, part, sprite } of this.moving) {
      const frame = frames[floor];
      if (!frame) continue;
      sprite.scale.set(frame.scale / SCALE);
      sprite.position.set(
        frame.x + part.pivot.x * frame.scale,
        frame.y + part.pivot.y * frame.scale,
      );
    }
  }

  /** Where the train's door is on a floor that has one: arrivals step out there. */
  trainDoor(floor: number): number | null {
    const frame = this.frames[floor];
    if (!frame) return null;
    const train = this.moving.some((m) => m.floor === floor && m.part.motion === 'train');
    return train ? frame.x + TRAIN_DOOR_X * frame.scale : null;
  }

  /** `ms` is the view's clock; `progress` how much of the level's clock has run (0..1). */
  update(ms: number, state: GameState, progress: number): void {
    const hands = clockHands(progress);
    for (const m of this.moving) {
      switch (m.part.motion) {
        case 'sway':
          m.sprite.angle = this.still ? 0 : sway(ms, m.seed);
          break;
        case 'hour':
          m.sprite.angle = hands.hour;
          break;
        case 'minute':
          m.sprite.angle = hands.minute;
          break;
        case 'glow':
          m.sprite.alpha = this.alpha * (this.still ? 0.3 : screenGlow(ms, m.seed));
          break;
        case 'train':
          this.moveTrain(m, state);
          break;
      }
    }
  }

  private moveTrain(m: Moving, state: GameState): void {
    const frame = this.frames[m.floor];
    if (!frame) return;
    const away = this.still ? 0 : trainAway(state.elapsedMs, this.arrivalsOn(state, m.floor));
    m.sprite.x = frame.x + (m.part.pivot.x + away * TRAIN_RUN) * frame.scale;
    m.sprite.visible = away < 1;
  }

  private arrivalsOn(state: GameState, floor: number): readonly number[] {
    const known = this.arrivals.get(floor);
    if (known) return known;
    const list = state.passengers.filter((p) => p.origin === floor).map((p) => p.spawnMs);
    this.arrivals.set(floor, list);
    return list;
  }
}
